import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { X, Save, Loader2 } from "lucide-react";

const SHAREABLE = ["name", "role_title", "company", "email", "phone", "relationship", "years_known", "note"];

/**
 * Editor for an existing reference. Lets the CV owner fill in the details the
 * reference could not — or would not — provide through the public link, and
 * pick which fields end up in the exported PDF.
 */
export default function ReferenceEditModal({ referenceId, onClose, onSaved }) {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get(`/references`)
      .then(({ data }) => {
        if (cancel) return;
        const doc = (data.items || data).find((r) => r.id === referenceId);
        if (!doc) return onClose?.();
        setForm({
          name: doc.name || "",
          email: doc.email || "",
          phone: doc.phone || "",
          relationship: doc.relationship || "",
          role_title: doc.role_title || "",
          company: doc.company || "",
          years_known: doc.years_known || "",
          note: doc.note || "",
          language: doc.language || "nl",
          share_fields: doc.share_fields && doc.share_fields.length
            ? doc.share_fields
            : ["name", "role_title", "company", "email", "relationship"],
        });
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [referenceId, onClose]);

  const toggleField = (f) => setForm((prev) => ({
    ...prev,
    share_fields: prev.share_fields.includes(f)
      ? prev.share_fields.filter((x) => x !== f)
      : [...prev.share_fields, f],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/references/${referenceId}`, form);
      toast.success(t("refEditSaved"));
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rbSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      onClick={onClose}
      data-testid="ref-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F9F8F6] border border-neutral-900 w-full max-w-2xl my-8"
      >
        <div className="flex items-start justify-between p-5 border-b border-neutral-300">
          <div>
            <p className="ed-eyebrow">{t("refEditEyebrow")}</p>
            <h2 className="font-serif text-2xl mt-1">{t("refEditTitle")}</h2>
          </div>
          <button data-testid="ref-edit-close" onClick={onClose} className="p-1.5 hover:bg-neutral-100"><X size={16} /></button>
        </div>

        {loading || !form ? (
          <div className="p-10 text-center text-neutral-500">
            <Loader2 className="animate-spin mx-auto" size={20} />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {[
              ["name", "refFormName", "text"],
              ["role_title", "refFormRoleTitle", "text"],
              ["company", "refFormCompany", "text"],
              ["email", "refFormEmail", "email"],
              ["phone", "refFormPhone", "tel"],
              ["relationship", "refFormRelationship", "text"],
              ["years_known", "refFormYearsKnown", "text"],
            ].map(([k, keyName, type]) => (
              <div key={k}>
                <label className="ed-label mb-1 block">{t(keyName)}</label>
                <input
                  data-testid={`ref-edit-${k}`}
                  type={type}
                  className="ed-input w-full"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}

            <div>
              <label className="ed-label mb-1 block">{t("refFormNote")}</label>
              <textarea
                data-testid="ref-edit-note"
                rows={4}
                maxLength={600}
                className="ed-input w-full"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>

            <div className="border border-neutral-300 p-4">
              <p className="ed-label mb-1">{t("refEditShareTitle")}</p>
              <p className="text-xs text-neutral-500 mb-3 leading-relaxed">{t("refEditShareHint")}</p>
              <div className="grid grid-cols-2 gap-2">
                {SHAREABLE.map((f) => {
                  const on = form.share_fields.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      data-testid={`ref-edit-share-${f}`}
                      onClick={() => toggleField(f)}
                      className={`flex items-center gap-2 border px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] transition-colors ${on ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"}`}
                    >
                      <span className={`w-3 h-3 border ${on ? "bg-white border-white" : "border-neutral-400"}`} />
                      {t(`refField_${f}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 p-5 border-t border-neutral-300">
          <button data-testid="ref-edit-cancel" onClick={onClose} className="ed-btn ed-btn-outline">
            {t("cancel")}
          </button>
          <button data-testid="ref-edit-save" onClick={save} disabled={saving || loading} className="ed-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={1.7} />}
            {t("refEditSave")}
          </button>
        </div>
      </div>
    </div>
  );
}
